
import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, isMissingCredentials } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useLocalStorage } from '@/hooks/useLocalStorage';

const logDebug = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console.log(...args);
  }
};

const logError = (...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console.error(...args);
  }
};

export function useSession() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [welcomeShown, setWelcomeShown] = useLocalStorage<Record<string, boolean>>('welcome_shown', {});

  useEffect(() => {
    if (isMissingCredentials()) {
      logDebug('Development mode: Skipping Supabase authentication setup');
      setIsLoading(false);
      return;
    }
    
    const getInitialSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logError('Error getting session:', error);
          toast({
            title: 'Authentication Error',
            description: 'Failed to get session',
            variant: 'destructive',
          });
        }
        
        setSession(data.session);
        setUser(data.session?.user ?? null);
      } catch (err) {
        logError('Failed to initialize auth:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    getInitialSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Show welcome message when user confirms email via signup confirmation link
        if (event === 'SIGNED_IN' && newSession?.user) {
          const urlHash = window.location.hash;
          const hasConfirmationToken = urlHash.includes('type=signup');

          if (hasConfirmationToken) {
            const hasShownWelcome = welcomeShown[newSession.user.id] || false;

            if (!hasShownWelcome) {
              // Clear the hash to clean up URL
              window.history.replaceState(null, '', window.location.pathname);

              toast({
                title: 'Welcome to Discloser!',
                description: 'Your email has been confirmed. You can now start adding properties and generating summaries.',
                duration: 8000,
              });

              // Mark welcome as shown for this user
              setWelcomeShown(prev => ({
                ...prev,
                [newSession.user.id]: true,
              }));
            }
          }
        }
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setIsLoading(false);
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, [toast, welcomeShown, setWelcomeShown]);

  const isAuthenticated = Boolean(session?.user?.id);
  const isAuthReady = !isLoading;

  return { user, session, isLoading, isAuthenticated, isAuthReady };
}
